import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { RefreshCw, Loader2, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';

// Re-reads this product's Square link straight from Square and saves the
// refreshed tokens and Square Online details back onto the record. Sits on the
// product's admin record beside the Square identity fields, so a link that has
// gone stale can be repaired without re-importing a whole category. Every field
// is reported, so a value Square no longer returns shows as cleared rather than
// disappearing silently.
const fmt = (v) => (v === null || v === undefined || v === '' ? '— none —' : String(v));

export default function SquareRelinkPanel({ product, onRefreshed }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const run = async () => {
    setRunning(true);
    setError('');
    setResult(null);
    try {
      const res = await base44.functions.invoke('relinkProductToSquare', { product_id: product.id });
      const data = res?.data || {};
      if (!data.success) throw new Error(data.error || 'Re-link failed.');
      setResult(data);
      if (data.applied && onRefreshed) onRefreshed(data.applied);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Re-link failed.');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="border border-white/10 bg-white/[0.02] px-4 py-3 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-white/60 text-xs font-bold tracking-wider">RE-LINK TO SQUARE</p>
          <p className="text-white/30 text-[10px] mt-1 leading-relaxed">
            Re-reads this item's tokens and Square Online details from Square and saves them here. Cost price, VAT rate, visibility flags and every other Base44-only field are left untouched.
          </p>
        </div>
        <button
          type="button"
          onClick={run}
          disabled={running}
          className="shrink-0 inline-flex items-center gap-1.5 border border-primary/30 bg-primary/10 hover:bg-primary hover:text-white text-primary font-bold text-[10px] tracking-widest px-3 py-2 transition-colors disabled:opacity-50"
        >
          {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          {running ? 'RE-LINKING…' : 'RE-LINK'}
        </button>
      </div>

      {error && (
        <p className="text-red-400 text-[11px] flex items-start gap-1.5">
          <XCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{error}
        </p>
      )}

      {result?.changes && (
        <div className="border-t border-white/10 pt-3 space-y-1.5">
          <p className="text-white/40 text-[10px] font-bold tracking-widest flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5 text-green-400" />
            REFRESHED FROM SQUARE{result.item_name ? ` — ${result.item_name}` : ''}
          </p>
          {result.changes.map((c) => (
            <div key={c.key} className="flex items-start justify-between gap-3 text-[11px]">
              <span className="text-white/40">{c.label}</span>
              {c.changed ? (
                <span className="text-right font-mono">
                  <span className="text-white/30 line-through">{fmt(c.before)}</span>
                  <span className="text-white/40 mx-1">→</span>
                  <span className="text-green-400">{fmt(c.after)}</span>
                </span>
              ) : (
                <span className="text-white/30 font-mono text-right">{fmt(c.after)} · unchanged</span>
              )}
            </div>
          ))}
          <p className={`text-[11px] flex items-center gap-1.5 pt-1 ${result.published_to_square_online ? 'text-green-400/80' : 'text-amber-400/80'}`}>
            {result.published_to_square_online
              ? <CheckCircle2 className="w-3 h-3" />
              : <AlertTriangle className="w-3 h-3" />}
            {result.published_to_square_online
              ? 'Published to Square Online.'
              : 'Not published to Square Online — this item has no live storefront page.'}
          </p>
        </div>
      )}
    </div>
  );
}